import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import type { ActivityItem, ApprovalItem } from "@/lib/api/approvalsApi";
import { prettifyText } from "@/lib/approvals/types";
import { shortSummary } from "@/lib/approvals/summary";
import { agentDotClass } from "@/lib/agentColors";

/** Pestanas del subgrupo de cada agente. Un solo tipo para todo el segmented control. */
type InboxTab = "pending" | "resolved" | "activity";

interface ApprovalListProps {
  items: ApprovalItem[];
  selectedId: string | null;
  onSelect: (item: ApprovalItem) => void;
  loading: boolean;
  /** Agentes HITL que el usuario puede atender: cada uno tiene grupo SIEMPRE,
   * aunque no tenga pendientes (si no, sus resueltas serian inaccesibles). */
  agentIds?: string[];
  resolvedByAgent?: Record<string, ApprovalItem[]>;
  resolvedLoading?: Record<string, boolean>;
  resolvedHasMore?: Record<string, boolean>;
  onExpandResolved?: (agentId: string) => void;
  onLoadMoreResolved?: (agentId: string) => void;
  pendingHasMore?: boolean;
  /** Registro de actividad por agente (runs descartados que no generaron tarea). */
  activityByAgent?: Record<string, ActivityItem[]>;
  activityLoading?: Record<string, boolean>;
  activityHasMore?: Record<string, boolean>;
  /** Carga la siguiente pagina del registro (scroll infinito). */
  onLoadMoreActivity?: (agentId: string) => void;
  onExpandActivity?: (agentId: string) => void;
}

/**
 * Sentinel para scroll infinito: un div vacio al final de la lista de resueltas.
 * Cuando entra en viewport, dispara `onVisible`. El guard contra dobles disparos
 * vive en el hook (ignora si ya carga o no hay mas), asi que aqui basta con
 * observar y llamar. Se re-observa si el nodo cambia; se desconecta en cleanup.
 */
function LoadMoreSentinel({ onVisible }: { onVisible: () => void }) {
  const ref = useRef<HTMLDivElement | null>(null);
  // onVisible en un ref para no recrear el observer en cada render del padre.
  const cbRef = useRef(onVisible);
  cbRef.current = onVisible;

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) cbRef.current();
        }
      },
      { root: null, threshold: 0.1 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return <div ref={ref} aria-hidden="true" className="h-px w-full" />;
}

/** Timestamp (ms) de una aprobacion para ordenar; 0 si no tiene fecha. */
function itemTime(item: ApprovalItem): number {
  const t = item.created_at ? Date.parse(item.created_at) : NaN;
  return Number.isNaN(t) ? 0 : t;
}

/**
 * Agrupa las aprobaciones por agente (assistant_id) para el arbol del sidebar.
 * - `agentIds` (agentes HITL accesibles) siembra un grupo por agente SIEMPRE,
 *   aunque no tenga pendientes: sin grupo no habria pestana Resueltas y el
 *   historico seria inaccesible con la bandeja al dia.
 * - Dentro de cada agente: orden DESCENDENTE por fecha de creacion (las mas
 *   recientes primero), igual que las resueltas.
 * - Los grupos se ordenan por su aprobacion mas reciente (descendente); los
 *   grupos sin pendientes van al final, en el orden recibido (alfabetico).
 */
function groupByAgent(
  items: ApprovalItem[],
  agentIds: string[],
): { agentId: string; items: ApprovalItem[] }[] {
  const byAgent = new Map<string, ApprovalItem[]>();
  for (const id of agentIds) byAgent.set(id, []);
  for (const it of items) {
    const arr = byAgent.get(it.assistant_id) ?? [];
    arr.push(it);
    byAgent.set(it.assistant_id, arr);
  }
  const groups = [...byAgent.entries()].map(([agentId, list]) => ({
    agentId,
    items: [...list].sort((a, b) => itemTime(b) - itemTime(a)),
  }));
  // Grupo ordenado por su item mas reciente (descendente); sin items -> 0 (al final).
  const groupTime = (g: { items: ApprovalItem[] }) => (g.items.length ? itemTime(g.items[0]) : 0);
  groups.sort((a, b) => groupTime(b) - groupTime(a));
  return groups;
}

/** Una hoja del arbol: la aprobacion (sin repetir el agente, que va en el padre). */
function ApprovalLeaf({
  item,
  active,
  onSelect,
}: {
  item: ApprovalItem;
  active: boolean;
  onSelect: (item: ApprovalItem) => void;
}) {
  const { t } = useTranslation();
  const summary = shortSummary(item);
  return (
    <button
      onClick={() => onSelect(item)}
      className={`flex w-full flex-col gap-0.5 border-l-2 py-2 pl-4 pr-3 text-left transition-colors
        ${active
          ? "border-l-[var(--ring)] bg-[var(--muted)]"
          : "border-l-transparent hover:bg-[var(--muted)]"
        }`}
    >
      {/* Titular (proveedor/cliente) — o la accion si no hay datos. + id generado. */}
      <span className="flex min-w-0 items-center gap-2">
        <span className="truncate text-xs font-medium text-[var(--foreground)]">
          {summary.headline || prettifyText(item.action)}
        </span>
        {item.result_ref && (
          <span className="ml-auto shrink-0 rounded bg-[var(--muted)] px-1.5 py-0.5 font-mono text-[10px] text-[var(--muted-foreground)]">
            {item.result_ref}
          </span>
        )}
      </span>
      {/* Identificadores cortos (nº · fecha factura · total). */}
      {summary.detail && (
        <span className="truncate text-xs text-[var(--muted-foreground)]">{summary.detail}</span>
      )}
      {/* Aviso de copia duplicada: hay otra pendiente del mismo documento (llegaron a la
          vez, normalmente en el mismo correo). Ambas siguen pendientes; el revisor
          decide cual aprueba y rechaza la otra. */}
      {item.duplicate_of && (
        <span className="truncate text-[11px] font-medium text-[var(--warning,#b45309)]">
          {t("approvals.duplicateWarning")}
        </span>
      )}
      {/* Fecha y HORA en que se produjo (llegada a la bandeja). */}
      {item.created_at && (
        <span className="text-[11px] text-[var(--muted-foreground)] opacity-70">
          {new Date(item.created_at).toLocaleString(undefined, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      )}
    </button>
  );
}

export default function ApprovalList({
  items,
  selectedId,
  onSelect,
  loading,
  agentIds = [],
  resolvedByAgent = {},
  resolvedLoading = {},
  resolvedHasMore = {},
  onExpandResolved,
  onLoadMoreResolved,
  pendingHasMore = false,
  activityByAgent = {},
  activityLoading = {},
  activityHasMore = {},
  onLoadMoreActivity,
  onExpandActivity,
}: ApprovalListProps) {
  const { t } = useTranslation();
  const groups = useMemo(() => groupByAgent(items, agentIds), [items, agentIds]);
  // Nodo agente colapsable (por defecto ABIERTO). `agentCollapsed[agentId] === true`
  // => contraido; ausente => usar el default (abierto).
  const [agentCollapsed, setAgentCollapsed] = useState<Record<string, boolean>>({});
  const isAgentOpen = (agentId: string) => !agentCollapsed[agentId];
  const toggleAgent = (agentId: string) =>
    setAgentCollapsed((p) => ({ ...p, [agentId]: !p[agentId] }));

  // Pestaña activa por agente: "pending" (default) | "resolved" | "activity".
  // Solo se ve UNA lista.
  const [tabByAgent, setTabByAgent] = useState<Record<string, InboxTab>>({});

  // Vacio global SOLO si no hay ningun grupo que pintar (ni pendientes ni agentes
  // HITL accesibles). Con agentes pero 0 pendientes, los grupos se pintan igual
  // (pestana Pendientes vacia + Resueltas accesible).
  if (groups.length === 0) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center text-sm text-[var(--muted-foreground)]">
        {loading ? t("approvals.loading") : t("approvals.empty")}
      </div>
    );
  }

  return (
    <ul className="flex flex-col">
      {groups.map((group) => {
        const agentOpen = isAgentOpen(group.agentId);
        const tab = tabByAgent[group.agentId] ?? "pending";
        const resolved = resolvedByAgent[group.agentId];
        const resolvedIsLoading = resolvedLoading[group.agentId];
        const activity = activityByAgent[group.agentId];
        const activityIsLoading = activityLoading[group.agentId];

        // Cambiar a "resolved"/"activity" dispara la carga bajo demanda la primera vez
        // (aun sin datos y sin carga en curso). Volver a pending y de nuevo NO
        // re-fetch si ya esta cargado (lista !== undefined).
        const selectTab = (next: InboxTab) => {
          if (next === "resolved" && resolved === undefined && !resolvedIsLoading) {
            onExpandResolved?.(group.agentId);
          }
          if (next === "activity" && activity === undefined && !activityIsLoading) {
            onExpandActivity?.(group.agentId);
          }
          setTabByAgent((p) => ({ ...p, [group.agentId]: next }));
        };

        const tabId = (value: InboxTab) => `tab-${group.agentId}-${value}`;
        const panelId = `panel-${group.agentId}-${tab}`;

        const tabButton = (value: InboxTab, label: string, count: number | string) => {
          const activeTab = tab === value;
          return (
            <button
              role="tab"
              id={tabId(value)}
              aria-controls={`panel-${group.agentId}-${value}`}
              aria-selected={activeTab}
              onClick={() => selectTab(value)}
              className={`flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded px-2 py-1 text-[11px] font-semibold transition-colors
                ${activeTab
                  ? "bg-[var(--card)] text-[var(--foreground)] shadow-sm"
                  : "text-[var(--muted-foreground)] hover:text-[var(--foreground)]"
                }`}
            >
              <span className="truncate">{label}</span>
              {count !== "" && (
                <span className="text-[10px] font-medium text-[var(--muted-foreground)]">
                  {count}
                </span>
              )}
            </button>
          );
        };

        return (
          <li key={group.agentId} className="border-b border-[var(--border)]">
            {/* Nodo PADRE: el agente (punto de color + etiqueta + nº de pendientes). */}
            <button
              onClick={() => toggleAgent(group.agentId)}
              aria-expanded={agentOpen}
              className="flex w-full items-center gap-1.5 px-2 py-2 text-left text-xs font-semibold text-[var(--foreground)] hover:bg-[var(--muted)]"
            >
              {agentOpen ? <ChevronDown size={14} className="shrink-0" /> : <ChevronRight size={14} className="shrink-0" />}
              <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${agentDotClass(group.agentId)}`} />
              <span className="truncate">{t(`agents.${group.agentId}.label`, group.agentId)}</span>
              <span className="ml-auto rounded-full bg-[var(--muted)] px-1.5 text-[11px] font-medium text-[var(--muted-foreground)]">
                {group.items.length}
              </span>
            </button>

            {agentOpen && (
              <div className="flex flex-col">
                {/* Segmented control: solo se ve UNA lista (Pendientes, Resueltas o
                    Registro). Junto a el, en Resueltas y en Registro, un boton para
                    RECARGAR la lista a peticion (ninguna de las dos entra en el
                    polling; son historico). */}
                <div className="mx-2 mb-1 flex items-center gap-1">
                  {/* `min-w-0` es lo que garantiza que el boton de refresco NUNCA se
                      pierda: sin el, el tablist (flex-1) conserva min-width:auto, se
                      niega a bajar del ancho de sus etiquetas y desborda la fila
                      empujando al boton fuera del sidebar. Con min-w-0 cede el y las
                      etiquetas se truncan, que es lo recuperable. */}
                  <div
                    role="tablist"
                    className="flex min-w-0 flex-1 gap-1 rounded border border-[var(--border)] p-0.5"
                  >
                    {tabButton("pending", t("approvals.group.pending"), group.items.length)}
                    {tabButton("resolved", t("approvals.group.resolved"), "")}
                    {tabButton("activity", t("approvals.activity.tab"), "")}
                  </div>
                  {tab === "resolved" && (
                    <button
                      type="button"
                      onClick={() => onExpandResolved?.(group.agentId)}
                      disabled={resolvedIsLoading}
                      aria-label={t("approvals.refreshResolved")}
                      title={t("approvals.refreshResolved")}
                      className="shrink-0 rounded border border-[var(--border)] p-1.5 text-[var(--muted-foreground)] transition-colors hover:text-[var(--foreground)] disabled:opacity-50"
                    >
                      <RefreshCw size={13} className={resolvedIsLoading ? "animate-spin" : undefined} />
                    </button>
                  )}
                  {tab === "activity" && (
                    <button
                      type="button"
                      onClick={() => onExpandActivity?.(group.agentId)}
                      disabled={activityIsLoading}
                      aria-label={t("approvals.activity.refresh")}
                      title={t("approvals.activity.refresh")}
                      className="shrink-0 rounded border border-[var(--border)] p-1.5 text-[var(--muted-foreground)] transition-colors hover:text-[var(--foreground)] disabled:opacity-50"
                    >
                      <RefreshCw size={13} className={activityIsLoading ? "animate-spin" : undefined} />
                    </button>
                  )}
                </div>

                <div role="tabpanel" id={panelId} aria-labelledby={tabId(tab)}>
                  {tab === "pending" ? (
                    group.items.length === 0 ? (
                      <div className="py-2 pl-4 pr-3 text-xs text-[var(--muted-foreground)]">
                        {t("approvals.pendingEmpty")}
                      </div>
                    ) : (
                      <>
                        <ul className="flex flex-col">
                          {group.items.map((item) => (
                            <li key={item.correlation_id}>
                              <ApprovalLeaf
                                item={item}
                                active={item.correlation_id === selectedId}
                                onSelect={onSelect}
                              />
                            </li>
                          ))}
                        </ul>
                        {/* Aviso estatico: hay mas pendientes que las mostradas por el poll. */}
                        {pendingHasMore && (
                          <div className="py-2 pl-4 pr-3 text-center text-[11px] text-[var(--muted-foreground)] opacity-70">
                            {t("approvals.morePending")}
                          </div>
                        )}
                      </>
                    )
                  ) : tab === "activity" ? (
                    activityIsLoading && activity === undefined ? (
                      <div className="py-2 pl-4 pr-3 text-xs text-[var(--muted-foreground)]">
                        {t("approvals.activity.loading")}
                      </div>
                    ) : !activity || activity.length === 0 ? (
                      <div className="py-2 pl-4 pr-3 text-xs text-[var(--muted-foreground)]">
                        {t("approvals.activity.empty")}
                      </div>
                    ) : (
                      <>
                      <ul className="flex flex-col">
                        {activity.map((entry) => (
                          // Tipografia y espaciado IDENTICOS a ApprovalLeaf (las hojas
                          // de Pendientes/Resueltas): titular y cuerpo en text-xs, la
                          // linea de fecha en text-[11px]. Sin esto el Registro se veia
                          // con letra mas pequena que las otras dos pestanas.
                          <li
                            key={entry.id}
                            className="flex flex-col gap-0.5 border-t border-[var(--border)] py-2 pl-4 pr-3 text-xs"
                          >
                            {/* FALLBACK: un motivo de un agente futuro sin clave i18n
                                se muestra con su codigo crudo (legible), nunca vacio. */}
                            <span className="text-xs font-medium text-[var(--foreground)]">
                              {t(`approvals.activity.reason.${entry.reason}`, entry.reason)}
                            </span>
                            {/* SIN truncate: el origen (remitente + asunto) es lo que
                                identifica el correo concreto. Cortarlo obliga al
                                usuario a adivinar de que factura se trata, que es
                                justo lo que este registro viene a resolver. */}
                            {entry.origen && (
                              <span className="break-words text-xs text-[var(--muted-foreground)]">
                                {entry.origen}
                              </span>
                            )}
                            {entry.documento && (
                              <span className="break-all text-xs text-[var(--muted-foreground)]">
                                {entry.documento}
                              </span>
                            )}
                            {/* CUANDO paso: es lo primero que pregunta quien echa en
                                falta una factura. Mismo formato que las hojas de la
                                bandeja (ApprovalLeaf). */}
                            {entry.created_at && (
                              <span className="flex flex-wrap gap-x-2 text-[11px] text-[var(--muted-foreground)] opacity-70">
                                <span>
                                  {new Date(entry.created_at).toLocaleString(undefined, {
                                    day: "2-digit",
                                    month: "2-digit",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })}
                                </span>
                                {entry.sender && (
                                  <span className="break-words">
                                    {t("approvals.activity.sender")} {entry.sender}
                                  </span>
                                )}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                      {/* Cargando la siguiente pagina (sustituye al aviso estatico
                          "hay mas": ahora se cargan solas al bajar). */}
                      {activityIsLoading && (
                        <div className="py-2 pl-4 pr-3 text-center text-[11px] text-[var(--muted-foreground)] opacity-70">
                          {t("approvals.activity.loading")}
                        </div>
                      )}
                      {/* Sentinel de scroll infinito: solo si hay mas paginas. FUERA
                          del <ul> (es un <div>, no contenido valido de una lista) y
                          en la misma posicion que el de resueltas. */}
                      {activityHasMore[group.agentId] && (
                        <LoadMoreSentinel
                          onVisible={() => onLoadMoreActivity?.(group.agentId)}
                        />
                      )}
                      </>
                    )
                  ) : resolvedIsLoading && (resolved ?? []).length === 0 ? (
                    <div className="py-2 pl-4 pr-3 text-xs text-[var(--muted-foreground)]">
                      {t("approvals.resolvedLoading")}
                    </div>
                  ) : (resolved ?? []).length === 0 ? (
                    <div className="py-2 pl-4 pr-3 text-xs text-[var(--muted-foreground)]">
                      {t("approvals.resolvedEmpty")}
                    </div>
                  ) : (
                    <>
                      <ul className="flex flex-col">
                        {[...(resolved ?? [])]
                          .sort((a, b) => itemTime(b) - itemTime(a))
                          .map((item) => (
                            <li key={item.correlation_id}>
                              <ApprovalLeaf
                                item={item}
                                active={item.correlation_id === selectedId}
                                onSelect={onSelect}
                              />
                            </li>
                          ))}
                      </ul>
                      {/* Spinner mientras se carga la siguiente pagina (append). */}
                      {resolvedIsLoading && (
                        <div className="py-2 pl-4 pr-3 text-center text-[11px] text-[var(--muted-foreground)] opacity-70">
                          {t("approvals.resolvedLoading")}
                        </div>
                      )}
                      {/* Sentinel de scroll infinito: solo si hay mas paginas. */}
                      {resolvedHasMore[group.agentId] && (
                        <LoadMoreSentinel
                          onVisible={() => onLoadMoreResolved?.(group.agentId)}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
